import { useEffect, useMemo, useState } from "react";
import { Box, Collapse, IconButton, Paper, Typography } from "@mui/material";
import { ChevronRight, ExpandLess, ExpandMore, LowPriority } from "@mui/icons-material";
import { startOfDay, endOfDay, format, parseISO } from "date-fns";
import { useRecords } from "../../../../api/useRecords";
import { FIRST_EEG_SERVICE, GENERAL_EEG_SERVICE, NFB_SERVICE } from "../../../../utils/constants";
import { buildWidgetSessionBlocks } from "./buildWidgetSessionBlocks";
import { NeuroAssignTechsRow } from "./NeuroAssignTechsRow";
import { TechPool } from "./tech-pool/TechPool";
import { useBlockGapTechs } from "./useBlockGapTechs";
import { BlockGapTechs } from "./BlockGapTechs";
import { SessionPreview } from "../SessionPreview";
import { sessionsOptions } from "../../sessionsOptions";
import { CalendarEvent } from "../../../../components/calendar/calendar.types";
import { mainColor } from "../../../../theme";
import { GapTechDisplay, PriorityBlock, PrioritySession, RelativePosition } from "./types.nfbAssign";

const priorityFieldsQuery = [
	"id",
	"start_date",
	"end_date",
	"status",
	"is_first_session",
	"date_created",
	"checkedin_at",
	"service.id",
	"service.name",
	"treatment.client.client_id",
	"treatment.client.user_id.first_name",
	"treatment.client.user_id.last_name",
	"lead.first_name",
	"lead.last_name",
	"technician.user_id.id",
	"technician.user_id.first_name",
	"technician.user_id.last_name",
	"technician.user_id.avatar",
	"internal_meetings_participants.technicians_id.user_id.id"
].join(",");

// internal meetings are service-null session rows carrying participants (mirrors the backend isMeetingRow)
const isMeetingRow = (session: PrioritySession) =>
	session.service == null && (session.internal_meetings_participants?.length ?? 0) > 0;
const FLOATING_POSITION = { position: "fixed", top: 80, right: 24, zIndex: 1000 } as const;
const EMPTY_SESSIONS: Array<PrioritySession> = [];

const SLOT_TIME_STYLES: Record<RelativePosition, object> = {
	previous: { color: "#666666" },
	current: { color: "#2d5a7b", fontWeight: 700 },
	next: { color: "#666666" }
};

const buildDayFilter = (clinics: Array<number>, now: Date) => {
	const filter = {
		_and: [
			{ start_date: { _between: [startOfDay(now).toISOString(), endOfDay(now).toISOString()] } },
			{ status: { _in: ["confirmed", "awaiting_in_clinic", "in_session", "booked", "done"] } },
			{ clinic_id: { id: { _in: clinics } } },
			{
				_or: [
					{ service: { id: { _in: [NFB_SERVICE, GENERAL_EEG_SERVICE, FIRST_EEG_SERVICE] } } },
					{ internal_meetings_participants: { technicians_id: { _nnull: true } } }
				]
			}
		]
	};
	return filter;
};

export const NeuroAssignTechsWidget = ({ clinics }: { clinics: Array<number> }) => {
	const [isMinimized, setIsMinimized] = useState(false);
	const [previewIsOpen, setPreviewIsOpen] = useState(false);
	const [selectedSession, setSelectedSession] = useState<PrioritySession | undefined>(undefined);
	const [collapsedSlots, setCollapsedSlots] = useState<Record<string, boolean>>({});
	const now = useCurrentTime();

	const dayFilter = buildDayFilter(clinics, now);

	// no fetchInterval: the sessions websocket invalidates this on change (useSessionsRecordsInvalidate)
	const { data, refetch } = useRecords("sessions", priorityFieldsQuery, {
		extraOptions: { filter: dayFilter, pageSize: 500 },
		enabled: clinics.length > 0 && !isMinimized
	});
	// meetings ride along in the same fetch; split them out so only real sessions reach the blocks
	const { sessions, meetings } = useMemo(() => {
		const rows = (data?.items ?? EMPTY_SESSIONS) as Array<PrioritySession>;
		return { sessions: rows.filter((row) => !isMeetingRow(row)), meetings: rows.filter(isMeetingRow) };
	}, [data]);
	const blocks = useMemo(() => buildWidgetSessionBlocks(sessions, now), [sessions, now]);
	const currentBlock = blocks.find((block) => block.relative === "current");

	const gapTechsByBlock = useBlockGapTechs(clinics[0], blocks, now, !isMinimized);

	const toggleMinimized = () => setIsMinimized((previous) => !previous);

	const openPreview = (session: PrioritySession) => {
		setSelectedSession(session);
		setPreviewIsOpen(true);
	};

	const toggleSlot = (slotLabel: string) => {
		setCollapsedSlots((previous) => ({ ...previous, [slotLabel]: !previous[slotLabel] }));
	};

	if (isMinimized) return <MinimizedWidget onExpand={toggleMinimized} />;

	return (
		<>
			<Paper
				elevation={8}
				sx={{
					...FLOATING_POSITION,
					width: 380,
					maxHeight: "75vh",
					borderRadius: 4,
					overflow: "hidden",
					display: "flex",
					flexDirection: "column"
				}}
			>
				<WidgetHeader onMinimize={toggleMinimized} />

				<Box
					sx={{
						maxHeight: "calc(75vh - 80px)",
						overflowY: "auto",
						p: 2,
						display: "flex",
						flexDirection: "column",
						gap: 2,
						backgroundColor: "#fafafa"
					}}
				>
					<SessionBlocks
						blocks={blocks}
						collapsedSlots={collapsedSlots}
						gapTechsByBlock={gapTechsByBlock}
						onToggleSlot={toggleSlot}
						onOpenPreview={openPreview}
					/>

					<TechPool
						sessions={sessions}
						meetings={meetings}
						currentBlock={currentBlock}
						clinics={clinics}
						now={now}
						onOpenPreview={openPreview}
					/>
				</Box>
			</Paper>

			{previewIsOpen ? (
				<SessionPreview
					previewIsOpen={previewIsOpen}
					setPreviewIsOpen={setPreviewIsOpen}
					event={selectedSession as unknown as CalendarEvent}
					groupsOptions={sessionsOptions}
					refetchSessions={refetch}
				/>
			) : null}
		</>
	);
};

const MinimizedWidget = ({ onExpand }: { onExpand: () => void }) => {
	return (
		<Paper
			elevation={8}
			onClick={onExpand}
			sx={{
				...FLOATING_POSITION,
				borderRadius: 3,
				overflow: "hidden",
				cursor: "pointer",
				display: "flex",
				alignItems: "center",
				gap: 1,
				px: 2,
				py: 1.5,
				background: mainColor,
				color: "white",
				"&:hover": { transform: "scale(1.02)" },
				transition: "transform 0.2s"
			}}
		>
			<LowPriority fontSize="small" />
			<Typography variant="body2" fontWeight={500}>
				Sessões
			</Typography>
			<ExpandMore fontSize="small" />
		</Paper>
	);
};

const WidgetHeader = ({ onMinimize }: { onMinimize: () => void }) => {
	return (
		<Box
			onClick={onMinimize}
			sx={{
				display: "flex",
				alignItems: "center",
				gap: 1.5,
				p: 2,
				background: mainColor,
				color: "white",
				cursor: "pointer"
			}}
		>
			<LowPriority />
			<Typography variant="subtitle1" fontWeight={600} sx={{ flexGrow: 1 }}>
				Sessões de NFB / EEG
			</Typography>
			<IconButton size="small" sx={{ color: "white" }}>
				<ExpandLess />
			</IconButton>
		</Box>
	);
};

type SessionBlocksProps = {
	blocks: Array<PriorityBlock>;
	collapsedSlots: Record<string, boolean>;
	gapTechsByBlock: Map<string, Array<GapTechDisplay>>;
	onToggleSlot: (slotLabel: string) => void;
	onOpenPreview: (session: PrioritySession) => void;
};

const SessionBlocks = ({
	blocks,
	collapsedSlots,
	gapTechsByBlock,
	onToggleSlot,
	onOpenPreview
}: SessionBlocksProps) => {
	if (blocks.length === 0) {
		return (
			<Typography variant="body2" sx={{ color: "#666666", textAlign: "center", py: 2 }}>
				Sem sessões hoje
			</Typography>
		);
	}

	return (
		<>
			{blocks.map((block) => {
				const isCollapsed = collapsedSlots[block.slotLabel] ?? block.relative === "next";
				return (
					<Box key={block.slotLabel}>
						<SessionBlockHeader
							block={block}
							isCollapsed={isCollapsed}
							onToggle={() => onToggleSlot(block.slotLabel)}
						/>
						<BlockGapTechs techs={gapTechsByBlock.get(block.slotLabel) ?? []} />
						<Collapse in={!isCollapsed}>
							<SessionBlockRows block={block} onOpenPreview={onOpenPreview} />
						</Collapse>
					</Box>
				);
			})}
		</>
	);
};

type SessionBlockHeaderProps = {
	block: PriorityBlock;
	isCollapsed: boolean;
	onToggle: () => void;
};

const SessionBlockHeader = ({ block, isCollapsed, onToggle }: SessionBlockHeaderProps) => {
	const slotEndLabel = getSlotEndLabel(block);
	const ChevronIcon = isCollapsed ? ChevronRight : ExpandMore;

	return (
		<Box
			onClick={onToggle}
			sx={{
				display: "flex",
				alignItems: "baseline",
				gap: 1,
				cursor: "pointer",
				mb: 1,
				opacity: block.relative !== "current" ? 0.62 : 1
			}}
		>
			<ChevronIcon sx={{ fontSize: 16, color: "#9e9e9e", alignSelf: "center" }} />
			<Typography
				variant="subtitle2"
				sx={{ fontVariantNumeric: "tabular-nums", ...SLOT_TIME_STYLES[block.relative] }}
			>
				{block.slotLabel}
				{slotEndLabel ? ` – ${slotEndLabel}` : ""}
			</Typography>
			<Typography variant="caption" sx={{ color: "#9e9e9e", ml: "auto" }}>
				{block.sessions.length} sessões
			</Typography>
		</Box>
	);
};

const SessionBlockRows = ({
	block,
	onOpenPreview
}: {
	block: PriorityBlock;
	onOpenPreview: (session: PrioritySession) => void;
}) => {
	const isDimmed = (session: PrioritySession) => block.relative !== "current" && session.status !== "in_session";

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
			{block.sessions.map((session, index) => (
				<Box key={session.id} sx={{ opacity: isDimmed(session) ? 0.62 : 1 }}>
					<NeuroAssignTechsRow session={session} order={index + 1} onClick={() => onOpenPreview(session)} />
				</Box>
			))}
		</Box>
	);
};

const useCurrentTime = () => {
	const [now, setNow] = useState(() => new Date());
	useEffect(() => {
		const interval = setInterval(() => setNow(new Date()), 3 * 60 * 1000);
		return () => clearInterval(interval);
	}, []);
	return now;
};

const getSlotEndLabel = (block: PriorityBlock) => {
	const endDate = block.sessions[0]?.end_date;
	const endLabel = endDate ? format(parseISO(endDate), "HH:mm") : "";
	return endLabel;
};
